import { nonenumerable } from 'shared/decorators';
import { findTheOneTrueOutputLayer } from 'lbs-pi-platform-access/es/data-server/image-analysis/util';
import { createLogger } from 'shared/services/logger';
import { DRAW_MODE_NAMES } from 'shared/stores/annotations';
import { store } from 'shared/stores/core/decorators';
import { makeLegacyAnnotationsAdapter } from 'shared/stores/image-analysis/annotations/AnnotationsAdapter';
import { isRegionOnCurrentIaLayer } from 'shared/stores/image-analysis/helpers';

const logger = createLogger('@store(IA_ANNOTATIONS)');

/**
 * The IA Annotations Store manages logic around manipulating annotations used for image analysis.
 */
@store('IA_ANNOTATIONS')
export class IaAnnotationsStore {
  @nonenumerable
  __deleteIaRegionTransactions = new Transactions(DeleteIaRegionTransaction);

  getters = {
    iaOutputLayer ({state, get}) {
      const {selectedMacroId} = get('IA_MACROS');
      const {selectedImageId} = state('IMAGE');

      return this.getters.getIaOutputLayer({
        imageId: selectedImageId,
        macroId: selectedMacroId
      });
    },
    iaInputLayer ({get}) {
      const iaOutputLayer = this.getters.iaOutputLayer;
      // If there's no output then layer there's no input layer. Placing this check here also ensures allLayers is
      // defined.
      if (!iaOutputLayer) {
        return null;
      }

      const {allLayers} = get('ANNOTATIONS').currentState;
      return allLayers[iaOutputLayer.inputAnnotationId];
    },
    getIaOutputLayer: ({get}) => ({imageId, macroId}) => { // Get output layer by macro id (there's only one)
      if (!macroId || !imageId) {
        return null;
      }

      const {allLayers} = get('ANNOTATIONS').getState({imageId});

      return findTheOneTrueOutputLayer(macroId, Object.values(allLayers));
    }
  };

  remap = {
    IA_CURRENT_LAYERS_DELETED: 'IA_EVALUATE_LAYER_SELECTION',
    // When the IA layer is deselected, revert layer selection control back to the annotations store.
    IA_DESELECT_LAYER: 'SELECT_ANNOTATIONS_LAYER'
  };

  // When a region is added, check if it is a candidate for analysis
  BEFORE_REGION_SAVE ({get, dispatch}, region) {
    if (isRegionOnCurrentIaLayer(get, region)) {
      dispatch('IA_BEFORE_REGION_SAVE', region);
    }
  }

  REGION_SAVED ({get, dispatch}, region) {
    if (isRegionOnCurrentIaLayer(get, region)) {
      dispatch('IA_REGION_SAVED', region);
    }
  }

  async REGION_DELETED ({dispatch, get}, {region}) {
    // IA regions contribute data to slide results; when a region is deleted it's parent slide's IA data must be
    // recomputed. If an output region gets deleted we need to trigger reanalysis. In this WV workflow, we don't
    // expose the concept of input and output regions to the user, so deleting one should also trigger a deletion
    // of the other.
    // Rules:
    // 1. If it's an IA region (e.g. it is or has an input region), delete it's pair and wait for deletion
    //    a. If it's an orphaned input layer no further action is needed since the output layer is already current.
    // 2. If it's part of a transaction then ignore the event
    // Anything that isn't an output region is potentially an input region. Only output regions have markupImageId's.

    if (this.__deleteIaRegionTransactions.isProcessing(region)) {
      return;
    }

    let inputRegion;
    let outputRegion;
    let partnerRegion;
    const annotations = get('ANNOTATIONS');

    // Look for input and output regions and identify the region's "partner" region
    if (region.inputRegionId) {
      outputRegion = region;
      partnerRegion = inputRegion = annotations.getInputRegion(region);
    } else {
      inputRegion = region;
      const allOutputRegions = annotations.getOutputRegions(region);
      if (allOutputRegions.length > 1) {
        logger.warn('Found multiple output regions for inputRegion; this can cause problems.', inputRegion);
      }
      partnerRegion = outputRegion = allOutputRegions[0];
    }

    // If a partner doesn't exist then don't waste our time trying to delete it
    if (partnerRegion) {
      // Delete the partner region. Block in a transaction to prevent unwanted recursion
      const transactionId = this.__deleteIaRegionTransactions.start(inputRegion, outputRegion);
      await dispatch('DELETE_REGION', {region: partnerRegion});
      this.__deleteIaRegionTransactions.finish(transactionId);

      // Notify that we're done
      dispatch('IA_REGION_DELETED', {region: outputRegion});
    }
  }

  /* IA events */

  // Called when an output region is deleted: Requests reanalysis on the layer, or if no regions remain deletes the
  // IA layers associated with the region.
  IA_REGION_DELETED ({dispatch, state, get}, {region}) {
    const annotations = get('ANNOTATIONS');
    const outputLayer = annotations.getLayer(region);
    const inputLayer = annotations.getInputLayer(outputLayer);

    if (inputLayer) {
      // Deleting a region requires recalculating the analysis results. If there's more than one region left on the input
      // layer, submit a new job. Otherwise, start fresh and delete both layers.
      if (inputLayer.regions.length > 0) {
        dispatch('IA_RUN_REANALYSIS', {region});
      } else {
        // If there's no regions left then clear the layer so the results don't appear in eSM
        dispatch('IA_DELETE_LAYERS', {inputLayer, outputLayer});
      }
    }
  }

  async IA_DELETE_CURRENT_LAYERS ({dispatch}) {
    const {iaInputLayer, iaOutputLayer} = this.getters;
    if (iaInputLayer && iaOutputLayer) {
      dispatch('IA_DELETE_LAYERS', {inputLayer: iaInputLayer, outputLayer: iaOutputLayer});
    }
  }

  // Command to delete IA Layers
  async IA_DELETE_LAYERS ({dispatch}, {inputLayer, outputLayer}) {
    if (!outputLayer || outputLayer.inputAnnotationId !== inputLayer.id) {
      throw new Error(`Cannot delete mismatched IA layers. InputId: ${inputLayer.id}, Output's InputId: ${outputLayer.inputAnnotationId}`);
    }

    const currentInputLayer = this.getters.iaInputLayer;
    const isCurrentLayer = currentInputLayer && inputLayer.id === currentInputLayer.id;

    await dispatch('DELETE_LAYERS', {layers: [inputLayer, outputLayer]});

    // If the current state's layers were deleted let others know.
    if (isCurrentLayer) {
      dispatch('IA_CURRENT_LAYERS_DELETED');
    }
  }

  async IA_SELECT_LAYER (store) {
    const {dispatch, get} = store;
    const laa = makeLegacyAnnotationsAdapter(store);

    // IA state change events (macroId/imageId change) attempt to set input/output layer. If this fails then we
    // should create them.
    if (!this.getters.iaOutputLayer || !this.getters.iaInputLayer) {
      const {syntheticKey} = get('IA_STATE');
      const lock = makeLock('us:ia:' + syntheticKey);
      dispatch('LOCK_DRAW_MODES', lock);
      await laa.createMacroLayers(this.getters.iaInputLayer, this.getters.iaOutputLayer);
      dispatch('UNLOCK_DRAW_MODES', lock);
    }

    // Inform the legacy annotations service to select our input layer. // TODO: Shouldn't this be dispatched to the annotationsStore?
    laa.annotationsService.layers.selectByProperty('Id', this.getters.iaInputLayer.id);
  };
}

function makeLock (lockOwner) {
  return {lockOwner, displayKey: 'messages.please-wait', modes: DRAW_MODE_NAMES};
}

/**
 * Validator for a "Delete IA Region" event. IA Regions are deleted in pairs (in any order) and there's only one
 * DELETE_REGION handler. To prevent unwanted recursion this tracks both input and output regions.
 */
class DeleteIaRegionTransaction {
  constructor (inputRegion, outputRegion) {
    if (!inputRegion || !outputRegion) {
      throw new Error('Cannot create DeleteIaRegionTransaction without input and output regions');
    }
    this.inputRegion = inputRegion;
    this.outputRegion = outputRegion;
  }

  isMatch ({id}) {
    return this.inputRegion.id === id || this.outputRegion.id === id;
  }
}

/**
 * Used to track whether a block of work is being processed.
 * To Consider: This is actually kinda handy. Maybe use in other re-entrant functions.
 */
class Transactions {
  __active = [];
  __Type;

  constructor (Type) {
    this.__Type = Type;
  }

  start (...args) {
    return this.__active.push(new this.__Type(...args)) - 1;
  }

  isProcessing (region) {
    return this.__active.find(t => t && t.isMatch(region));
  }

  finish (transactionId) {
    delete this.__active[transactionId];
  }
}



// WEBPACK FOOTER //
// ./src/shared/stores/image-analysis/annotations/IaAnnotationsStore.js